/**
 * ArchiveSearchService — 완료된 태스크(완료 문서) 검색 서비스
 *
 * Tasks 보드 안에서 status='done' + completion 문서가 채워진 카드들을
 * 키워드/카테고리/태그/날짜/참여자 기준으로 검색한다.
 *
 * - 키워드 검색: title + plain_text ilike
 * - 시맨틱 검색: Cloud Functions(Vertex AI) + pgvector 코사인 유사도
 * - 임베딩은 완료된 태스크 post.id 자체를 가리킨다
 */
import { supabase } from './SupabaseClient';
import { Post, rowToPost } from '../models/Board';

function getClient() {
  if (!supabase) throw new Error('[ArchiveSearchService] Supabase 클라이언트가 초기화되지 않았습니다.');
  return supabase;
}

/** 완료 문서 검색 필터 */
export interface ArchiveFilters {
  category?: string;
  tags?: string[];
  participantUids?: string[];
  dateRange?: { start: string; end: string };
}

/** 완료 문서 검색 결과 */
export interface ArchiveSearchResult {
  posts: Post[];
  total: number;
}

/** 필터 옵션 (UI에서 표시할 카테고리/태그/참여자 목록) */
export interface ArchiveFilterOptions {
  categories: string[];
  tags: string[];
  participants: Array<{ uid: string; name: string }>;
}

/**
 * 완료된 태스크 키워드 검색 + 필터
 *
 * Tasks 보드 안에서 metadata.status='done' 카드만 대상으로 한다.
 */
export async function searchArchive(
  boardId: string,
  query: string,
  filters: ArchiveFilters = {},
  options?: { page?: number; pageSize?: number },
): Promise<ArchiveSearchResult> {
  const sb = getClient();
  const page = options?.page ?? 0;
  const pageSize = options?.pageSize ?? 50;
  const from = page * pageSize;
  const to = from + pageSize - 1;

  let q = sb
    .from('posts')
    .select('*', { count: 'exact' })
    .eq('board_id', boardId)
    .eq('metadata->>status', 'done')
    .order('created_at', { ascending: false })
    .range(from, to);

  // 키워드 검색
  if (query.trim()) {
    const pattern = `%${query.trim()}%`;
    q = q.or(`title.ilike.${pattern},plain_text.ilike.${pattern}`);
  }

  // 카테고리 필터 (metadata.completion.category)
  if (filters.category) {
    q = q.eq('metadata->completion->>category', filters.category);
  }

  // 태그 필터 (posts.tags 배열 contains)
  if (filters.tags && filters.tags.length > 0) {
    q = q.contains('tags', filters.tags);
  }

  // 날짜 범위 필터 (완료 시점 = metadata.completion.period_end)
  if (filters.dateRange?.start) {
    q = q.gte('metadata->completion->>period_end', filters.dateRange.start);
  }
  if (filters.dateRange?.end) {
    q = q.lte('metadata->completion->>period_end', `${filters.dateRange.end}T23:59:59.999Z`);
  }

  const { data, error, count } = await q;
  if (error) throw error;

  let posts = (data ?? []).map(rowToPost);

  // 참여자 필터는 JSONB 배열 내부 검색이라 클라이언트 사이드에서 처리
  if (filters.participantUids && filters.participantUids.length > 0) {
    const uids = new Set(filters.participantUids);
    posts = posts.filter(p => {
      const participants = p.metadata?.completion?.participants as Array<{ uid: string }> | undefined;
      if (!participants) return false;
      return participants.some(pt => uids.has(pt.uid));
    });
  }

  return {
    posts,
    total: count ?? posts.length,
  };
}

/**
 * 완료된 태스크에서 사용 가능한 필터 옵션 추출
 *
 * Tasks 보드의 status='done' 카드들의 completion 메타데이터에서
 * 카테고리, 태그, 참여자를 수집한다.
 */
export async function getArchiveFilterOptions(
  boardId: string,
): Promise<ArchiveFilterOptions> {
  const sb = getClient();

  const { data, error } = await sb
    .from('posts')
    .select('tags, metadata')
    .eq('board_id', boardId)
    .eq('metadata->>status', 'done');

  if (error) throw error;

  const categories = new Set<string>();
  const tags = new Set<string>();
  const participantMap = new Map<string, string>(); // uid → name

  for (const row of data ?? []) {
    const completion = (row.metadata as { completion?: any } | null)?.completion;

    if (completion?.category) {
      categories.add(completion.category);
    }

    if (Array.isArray(row.tags)) {
      for (const tag of row.tags) {
        tags.add(tag);
      }
    }

    if (Array.isArray(completion?.participants)) {
      for (const p of completion.participants) {
        if (p.uid && p.name) {
          participantMap.set(p.uid, p.name);
        }
      }
    }
  }

  return {
    categories: Array.from(categories).sort(),
    tags: Array.from(tags).sort(),
    participants: Array.from(participantMap.entries())
      .map(([uid, name]) => ({ uid, name }))
      .sort((a, b) => a.name.localeCompare(b.name)),
  };
}

/**
 * Cloud Functions 'generateEmbedding'을 호출하여 쿼리 텍스트의 768차원 벡터를 획득합니다.
 *
 * Vertex AI 기반 — GCP 서비스 계정 자동 인증 (API 키 불필요)
 */
async function fetchQueryEmbedding(queryText: string): Promise<number[]> {
  const { getFunctions, httpsCallable } = await import('firebase/functions');
  const { getApp } = await import('firebase/app');
  const functions = getFunctions(getApp(), 'asia-northeast1');
  const callable = httpsCallable<
    { text: string; action: string },
    { embedding: number[] }
  >(functions, 'generateEmbedding');

  const result = await callable({ text: queryText, action: 'query' });
  return result.data.embedding;
}

/**
 * 완료된 태스크의 임베딩을 생성하여 DB에 저장합니다.
 *
 * TaskCompleteModal에서 완료 처리 후 호출하여 시맨틱 검색에 인덱싱합니다.
 * Cloud Functions 내에서 Supabase DB(archive_embeddings)에 직접 upsert합니다.
 *
 * @param postId - 완료된 태스크 post ID (boards.type='tasks', status='done')
 * @param text - 임베딩 소스 텍스트 (제목 + 요약 + 결과)
 */
export async function storeArchiveEmbedding(
  postId: string,
  text: string,
): Promise<void> {
  try {
    const { getFunctions, httpsCallable } = await import('firebase/functions');
    const { getApp } = await import('firebase/app');
    const functions = getFunctions(getApp(), 'asia-northeast1');
    const callable = httpsCallable<
      { text: string; action: string; postId: string },
      { embedding: number[] }
    >(functions, 'generateEmbedding');

    await callable({ text, action: 'store', postId });
  } catch (err) {
    console.warn('[ArchiveSearchService] 임베딩 저장 실패 (무시):', err);
  }
}

/**
 * 시맨틱 검색 — pgvector 코사인 유사도 기반
 *
 * 1. Cloud Functions(Vertex AI)으로 쿼리 임베딩 생성
 * 2. match_archive_embeddings RPC로 유사도 검색
 * 3. 결과 post_id로 게시글 데이터 조회 (status='done' 필터 적용)
 *
 * 임베딩 생성 실패 시 키워드 검색으로 자동 fallback합니다.
 */
export async function searchBySemantic(
  boardId: string,
  queryText: string,
  filters: ArchiveFilters = {},
): Promise<ArchiveSearchResult> {
  try {
    const embedding = await fetchQueryEmbedding(queryText);

    const sb = getClient();
    const { data: matches, error: rpcError } = await sb.rpc(
      'match_archive_embeddings',
      {
        query_embedding: `[${embedding.join(',')}]`,
        match_threshold: 0.4,
        match_count: 20,
        filter_board_id: boardId,
      },
    );

    if (rpcError) throw rpcError;
    if (!matches || matches.length === 0) {
      return { posts: [], total: 0 };
    }

    const postIds = matches.map((m: { post_id: string }) => m.post_id);
    const { data: posts, error: postError } = await sb
      .from('posts')
      .select('*')
      .in('id', postIds)
      .eq('metadata->>status', 'done');

    if (postError) throw postError;

    const postMap = new Map((posts ?? []).map(p => [p.id, p]));
    let sortedPosts = postIds
      .map((id: string) => postMap.get(id))
      .filter(Boolean)
      .map(rowToPost);

    if (filters.category) {
      sortedPosts = sortedPosts.filter((p: Post) =>
        p.metadata?.completion?.category === filters.category
      );
    }
    if (filters.tags && filters.tags.length > 0) {
      const requiredTags = new Set(filters.tags);
      sortedPosts = sortedPosts.filter((p: Post) =>
        p.tags?.some((t: string) => requiredTags.has(t))
      );
    }

    return {
      posts: sortedPosts,
      total: sortedPosts.length,
    };
  } catch (err) {
    console.warn('[ArchiveSearchService] 시맨틱 검색 fallback:', err);
    return searchArchive(boardId, queryText, filters);
  }
}
